"use client";

import React from "react";
import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { Info, ClipboardType, Star, ArrowLeft, ShoppingBag, Heart, X, HelpCircle } from "lucide-react";
import { getProductDescription, getProductLanguage, getProductPrice, getProductCoin, useT } from "@/context/I18nContext";
import { useContextElement } from "@/context/Context";
import { fetchWrapper } from "@/helpers";
import ReviewsSection from "./reviews/ReviewsSection";
import QuestionsSection from "./questions/QuestionsSection";

function CollapsibleContent({ children }) {
	const innerRef = useRef(null);
	const [needsToggle, setNeedsToggle] = useState(false);
	const [expanded, setExpanded] = useState(false);
	const { t } = useT();

	useEffect(() => {
		const el = innerRef.current;
		if (!el || expanded || typeof ResizeObserver === "undefined") return;

		const check = () => setNeedsToggle(el.scrollHeight - el.clientHeight > 4);
		check();
		const observer = new ResizeObserver(check);
		observer.observe(el);
		return () => observer.disconnect();
	}, [expanded]);

	return (
		<div className='tf-collapsible'>
			<div ref={innerRef} className={`tf-collapsible-inner ${expanded ? "is-expanded" : ""}`}>
				{children}
				{needsToggle && !expanded && <div className='tf-collapsible-shadow' />}
			</div>
			{needsToggle && (
				<button type='button' className='tf-collapsible-toggle' onClick={() => setExpanded((v) => !v)}>
					{expanded ? t("produs_vezi_mai_putin") : t("produs_vezi_mai_mult")}
				</button>
			)}
		</div>
	);
}

export default function ShopDetailsTab({ product }) {
	const { t } = useT();
	const router = useRouter();
	const { addToWishlist, isAddedtoWishlist, addProductToCart, isAddedToCartProducts } = useContextElement();

	const [counts, setCounts] = useState({ reviewsCount: null, questionsCount: null });

	const tabs = [
		{ icon: <ClipboardType />, title: t("produs_descriere"), active: true },
		{ icon: <Info />, title: t("produs_informatii_suplimentare"), active: false },
		{ icon: <Star />, title: t("produs_recenzii"), active: false, count: counts.reviewsCount },
		{ icon: <HelpCircle />, title: t("produs_intrebari"), active: false, count: counts.questionsCount },
	];

	const [currentTab, setCurrentTab] = useState(1);
	const [showStickyBar, setShowStickyBar] = useState(false);
	const [isTabsStuck, setIsTabsStuck] = useState(false);
	const [headerHeight, setHeaderHeight] = useState(0);
	const [isDesktop, setIsDesktop] = useState(false);
	const sectionRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];
	const scrollLockRef = useRef(false);
	const stickyBarRef = useRef(null);
	const tabsStickyAnchorRef = useRef(null);

	const tabsStickyOffset = showStickyBar ? stickyBarRef.current?.offsetHeight || 0 : headerHeight;

	useEffect(() => {
		fetchWrapper
			.get(`/api/engagement-counts/${product.id}`)
			.then((res) => setCounts({ reviewsCount: res.reviewsCount, questionsCount: res.questionsCount }))
			.catch(() => {});
	}, [product.id]);

	useEffect(() => {
		const mq = window.matchMedia("(min-width: 992px)");
		setIsDesktop(mq.matches);
		const onChange = (e) => setIsDesktop(e.matches);
		mq.addEventListener("change", onChange);
		return () => mq.removeEventListener("change", onChange);
	}, []);

	useEffect(() => {
		const headerEl = document.querySelector(".main-header") || document.querySelector("header");
		if (!headerEl || typeof ResizeObserver === "undefined") return;

		const observer = new ResizeObserver(([entry]) => setHeaderHeight(entry.target.getBoundingClientRect().height));
		observer.observe(headerEl);
		return () => observer.disconnect();
	}, []);

	useEffect(() => {
		const onScroll = () => setShowStickyBar(window.scrollY > 120);
		onScroll();
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => window.removeEventListener("scroll", onScroll);
	}, []);

	useEffect(() => {
		const headerEl = document.querySelector("header");
		headerEl?.classList.toggle("tf-header-hidden", showStickyBar && isDesktop);
		return () => headerEl?.classList.remove("tf-header-hidden");
	}, [showStickyBar, isDesktop]);

	useEffect(() => {
		const handleScroll = () => {
			const anchor = tabsStickyAnchorRef.current;
			if (anchor) setIsTabsStuck(isDesktop && anchor.getBoundingClientRect().top <= tabsStickyOffset);

			if (scrollLockRef.current) return;

			const line = tabsStickyOffset + 40;
			let activeIdx = 0;
			sectionRefs.forEach((r, idx) => {
				if (!r.current) return;
				if (r.current.getBoundingClientRect().top - line <= 0) activeIdx = idx;
			});
			setCurrentTab(activeIdx + 1);
		};

		handleScroll();
		window.addEventListener("scroll", handleScroll, { passive: true });
		return () => window.removeEventListener("scroll", handleScroll);
	}, [tabsStickyOffset, isDesktop]);

	const scrollToTab = (i) => {
		setCurrentTab(i + 1);
		const target = sectionRefs[i].current;
		if (!target) return;

		scrollLockRef.current = true;
		target.scrollIntoView({ behavior: "smooth", block: "start" });
		window.setTimeout(() => {
			scrollLockRef.current = false;
		}, 700);
	};

	const ProductSpecifications = ({ specifications, sectionRef }) => {
		if (!specifications || specifications.length === 0) return null;

		const data = specifications[0];
		const rowIndexRef = { current: 0 };

		const renderSpecs = (obj, parentKey = "") => {
			// Dacă obj e array, procesează fiecare element
			if (Array.isArray(obj)) {
				return obj.map((item, index) => (
					<React.Fragment key={index}>{renderSpecs(item, parentKey)}</React.Fragment>
				));
			}

			return Object.entries(obj).map(([key, value]) => {
				const formattedKey = key.replace(/_/g, " ").toUpperCase();

				if (typeof value === "object" && !Array.isArray(value) && value !== null) {
					return (
						<React.Fragment key={key}>
							<tr
								style={{
									border: "1px solid black",
									padding: "8px",
									backgroundColor: "#f2f2f2",
								}}>
								<th className='tf-attr-label' colSpan={2}>
									<b style={{ color: "black" }}>{formattedKey}</b>
								</th>
							</tr>
							{renderSpecs(value, key)}
						</React.Fragment>
					);
				} else {
					const isAlt = rowIndexRef.current % 2 === 1;
					rowIndexRef.current += 1;
					return (
						<tr key={key} className={isAlt ? "tf-attr-row-alt" : ""}>
							<th className='tf-attr-label' style={{ color: "black" }}>
								{formattedKey}
							</th>
							<td className='tf-attr-value' style={{ color: "black" }}>
								{Array.isArray(value) ? value.join(", ") : value?.toString() ?? ""}
							</td>
						</tr>
					);
				}
			});
		};

          const specs = typeof specifications === "string"
          ? JSON.parse(specifications) 
          : specifications;

		return (
			<div ref={sectionRef} id='tab-section-2' className={`widget-content-inner ${currentTab == 2 ? "active" : ""} `}>
				<h2 className='tf-product-section-title'>{t("produs_informatii_suplimentare")}</h2>
				<CollapsibleContent>
					<div className='tf-specs-table-wrap'>
						<table className='tf-pr-attrs'>
							<tbody>{renderSpecs(specs)}</tbody>
						</table>
					</div>
				</CollapsibleContent>
			</div>
		);
	};

	return (
		<>
			<div
				ref={stickyBarRef}
				className={`product-sticky-bar tf-md-hidden ${showStickyBar ? "is-visible" : ""}`}>
				<div className='container-full px_15 lg-px_40'>
					<div className='product-sticky-bar-inner'>
						<button
							type='button'
							className='product-sticky-bar-back'
							onClick={() => router.back()}
							aria-label={t("produs_inapoi")}
							style={{
								appearance: "none",
								WebkitAppearance: "none",
								boxShadow: "none",
								backgroundImage: "none",
								flexShrink: 0,
								width: 40,
								height: 40,
								borderRadius: "50%",
								border: "1px solid #e5e5e5",
								background: "#fff",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}>
							<ArrowLeft size={20} />
						</button>
						{product.images && (
							<img src={product.images.split("|")[0]} alt='' className='product-sticky-bar-thumb' />
						)}
						<div className='product-sticky-bar-name'>{getProductLanguage(product)}</div>
						<div className='product-sticky-bar-price'>
							{product.priceReduced ? getProductPrice(product.priceReduced) : getProductPrice(product.price)} {getProductCoin()}
						</div>
						<button
							type='button'
							className='product-sticky-bar-cart'
							onClick={() => addProductToCart(product.id, 1)}
							style={{
								appearance: "none",
								WebkitAppearance: "none",
								boxShadow: "none",
								backgroundImage: "none",
								borderRadius: 10,
								border: 0,
								background: isAddedToCartProducts(product.id) ? "#db1215" : "#3b82f6",
								color: "#fff",
							}}>
							{isAddedToCartProducts(product.id) ? <X size={18} /> : <ShoppingBag size={18} />}
							{isAddedToCartProducts(product.id) ? t("produs_scoate_din_cos") : t("produs_adauga_in_cos")}
						</button>
						<button
							type='button'
							className='product-sticky-bar-wishlist'
							onClick={() => addToWishlist(product.id)}
							aria-label={t("produs_adauga_favorite")}
							style={{
								appearance: "none",
								WebkitAppearance: "none",
								boxShadow: "none",
								backgroundImage: "none",
								flexShrink: 0,
								width: 40,
								height: 40,
								borderRadius: 10,
								border: "1px solid #e5e5e5",
								background: "#fff",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}>
							<Heart
								size={18}
								fill={isAddedtoWishlist(product.id) ? "#e53e3e" : "none"}
								color={isAddedtoWishlist(product.id) ? "#e53e3e" : "#6b7280"}
							/>
						</button>
					</div>
				</div>
			</div>

			<section className='flat-spacing-17 pt_0' style={{ maxWidth: "100vw", overflow: "clip" }}>
				<div className='widget-tabs style-has-border style-scrollspy'>
					<div ref={tabsStickyAnchorRef} />
					<ul
						className={`widget-menu-tab container-full px_15 lg-px_40 tf-product-page-container ${isTabsStuck ? "is-stuck" : ""}`}
						style={{ top: tabsStickyOffset }}>
						{tabs.map((elm, i) => (
							<li
								key={i}
								onClick={() => scrollToTab(i)}
								className={`item-title ${currentTab == i + 1 ? "active" : ""} `}>
								<span className='inner'>
									{elm.icon} {elm.title}
									{elm.count != null && <span className='tf-tab-badge'>{elm.count}</span>}
								</span>
							</li>
						))}
					</ul>
					<div className='container-full px_15 lg-px_40 tf-product-page-container'>
						<div className='widget-content-tab'>
							<div ref={sectionRefs[0]} id='tab-section-1' className={`widget-content-inner ${currentTab == 1 ? "active" : ""} `}>
								<h2 className='tf-product-section-title'>{t("produs_descriere")}</h2>
								<CollapsibleContent>
									<p
										className='mb_30'
										dangerouslySetInnerHTML={{
											__html: getProductDescription(product),
										}}
									/>
								</CollapsibleContent>
							</div>

							<ProductSpecifications specifications={product.specifications} sectionRef={sectionRefs[1]} />

							<div ref={sectionRefs[2]} id='tab-section-3' className={`widget-content-inner ${currentTab == 3 ? "active" : ""} `}>
								<h2 className='tf-product-section-title'>{t("produs_recenzii")}</h2>
								<ReviewsSection product={product} />
							</div>

							<div ref={sectionRefs[3]} id='tab-section-4' className={`widget-content-inner ${currentTab == 4 ? "active" : ""} `}>
								<h2 className='tf-product-section-title'>{t("produs_intrebari")}</h2>
								<QuestionsSection product={product} />
							</div>
						</div>
					</div>
				</div>
			</section>
		</>
	);
}
